import {
  Description,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Field,
  Label,
  Switch,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { type FormEvent } from 'react'
import { Address } from '../../generated'
import { type RecurrenceState } from '../../utils/Rrule.ts'
import RecurrenceBuilder from '../clubAdmin/RecurrenceBuilder.tsx'

export interface ScheduleFormState {
  name: string
  recurrence: RecurrenceState
  startDate: string // 'YYYY-MM-DD'
  startTime: string // 'HH:mm'
  durationMin: number
  isRegularEvent: boolean
  addressId: number | ''
}

export interface ScheduleFormDialogProps {
  isOpen: boolean
  mode: 'create' | 'edit'
  onClose: () => void
  formState: ScheduleFormState
  addresses: Address[]
  onFieldChange: <K extends keyof ScheduleFormState>(
    field: K,
    value: ScheduleFormState[K]
  ) => void
  onSubmit: () => void
  isSubmitting: boolean
  error?: string | null
}

const inputClassName =
  'mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600'

const labelClassName = 'block text-sm font-medium text-gray-900'

const ScheduleFormDialog = ({
  isOpen,
  mode,
  onClose,
  formState,
  addresses,
  onFieldChange,
  onSubmit,
  isSubmitting,
  error,
}: ScheduleFormDialogProps) => {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit()
  }

  const hasAddresses = addresses.length > 0

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-5 py-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
            <div className="flex items-start justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-gray-900">
                {mode === 'edit' ? 'Edit meeting' : 'Add a meeting'}
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="-m-2 rounded-md p-2 text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Set up a recurring meeting so members see it on the club's public
              schedule.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="meeting-name" className={labelClassName}>
                  Meeting name
                </label>
                <input
                  id="meeting-name"
                  name="name"
                  type="text"
                  value={formState.name}
                  onChange={(event) => onFieldChange('name', event.target.value)}
                  placeholder="e.g. Weekly Club Meeting"
                  className={inputClassName}
                  required
                />
              </div>

              <RecurrenceBuilder
                value={formState.recurrence}
                onChange={(recurrence) =>
                  onFieldChange('recurrence', recurrence)
                }
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="meeting-start-date" className={labelClassName}>
                    First occurrence date
                  </label>
                  <input
                    id="meeting-start-date"
                    name="startDate"
                    type="date"
                    value={formState.startDate}
                    onChange={(event) =>
                      onFieldChange('startDate', event.target.value)
                    }
                    className={inputClassName}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="meeting-start-time" className={labelClassName}>
                    Start time
                  </label>
                  <input
                    id="meeting-start-time"
                    name="startTime"
                    type="time"
                    value={formState.startTime}
                    onChange={(event) =>
                      onFieldChange('startTime', event.target.value)
                    }
                    className={inputClassName}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="meeting-duration" className={labelClassName}>
                  Duration (minutes)
                </label>
                <input
                  id="meeting-duration"
                  name="durationMin"
                  type="number"
                  min={1}
                  step={1}
                  value={formState.durationMin}
                  onChange={(event) =>
                    onFieldChange('durationMin', Number(event.target.value))
                  }
                  className={inputClassName}
                  required
                />
              </div>

              <div>
                <label htmlFor="meeting-address" className={labelClassName}>
                  Location
                </label>
                <select
                  id="meeting-address"
                  name="addressId"
                  value={formState.addressId}
                  onChange={(event) =>
                    onFieldChange(
                      'addressId',
                      event.target.value === ''
                        ? ''
                        : Number(event.target.value)
                    )
                  }
                  className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-gray-50`}
                  disabled={!hasAddresses}>
                  <option value="">Select an address</option>
                  {addresses.map((address) => (
                    <option key={address.id} value={address.id}>
                      {address.name}
                    </option>
                  ))}
                </select>
                {hasAddresses ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Manage locations in the Club addresses section above.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-amber-600">
                    Add an address first — every meeting needs a location. Use
                    the Club addresses section above.
                  </p>
                )}
              </div>

              <Field className="flex items-start gap-3">
                <Switch
                  checked={formState.isRegularEvent}
                  onChange={(checked: boolean) =>
                    onFieldChange('isRegularEvent', checked)
                  }
                  className={`${
                    formState.isRegularEvent ? 'bg-indigo-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600`}>
                  <span
                    aria-hidden="true"
                    className={`${
                      formState.isRegularEvent
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </Switch>
                <div>
                  <Label
                    as="span"
                    className="text-sm font-medium text-gray-900"
                    passive>
                    Regular event
                  </Label>
                  <Description
                    as="span"
                    className="block text-xs text-gray-500">
                    Show a "See details" button for members on this meeting.
                  </Description>
                </div>
              </Field>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  {isSubmitting ? 'Saving...' : 'Save meeting'}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default ScheduleFormDialog
